import {
  COL_PRODUCTION_REQUEST_LINES,
  COL_PRODUCTION_REQUESTS,
  COL_SCHEDULE_SLOTS,
} from '../config/collections'
import { pb } from '../pocketbase'
import { computeLineStatus, rollupRequestStatus } from './requestLineStatus'
import { hoursForProductQuantity } from './scheduleCalculations'
import { transitionSlotStatus } from './scheduleSlotLifecycle'
import { unreserveMaterialsForSlot, reserveMaterialsForSlot } from './inventoryReservation'
import type {
  MachineRecord,
  ProductRecord,
  ProductionRequestLineRecord,
  ScheduleSlotRecord,
  ScheduleSlotStatus,
} from '../types/records'

export type SlotTimeRange = {
  id: string
  plannedStart: string
  plannedEnd: string
  status: ScheduleSlotStatus
  machine?: string
  requestLine?: string
}

export type SlotTimePatch = { plannedStart: string; plannedEnd: string }
export type SlotPatch = SlotTimePatch & { machine?: string; shift?: string }

const SHIFTABLE: ScheduleSlotStatus[] = ['planned', 'in_progress']

export function isShiftableSlot(status: ScheduleSlotStatus): boolean {
  return SHIFTABLE.includes(status)
}

function toIso(ms: number): string {
  return new Date(ms).toISOString()
}

function ms(iso: string): number {
  return new Date(iso).getTime()
}

function sortedShiftable(slots: SlotTimeRange[]): SlotTimeRange[] {
  return slots
    .filter((s) => isShiftableSlot(s.status))
    .sort((a, b) => ms(a.plannedStart) - ms(b.plannedStart))
}

/** Сдвинуть слоты, начинающиеся не раньше pivotEnd, на deltaMs. */
export function computeShiftFromPivot(
  slots: SlotTimeRange[],
  pivotEndMs: number,
  deltaMs: number,
  excludeId?: string,
): Map<string, SlotTimePatch> {
  const updates = new Map<string, SlotTimePatch>()
  if (deltaMs === 0) return updates

  for (const slot of sortedShiftable(slots)) {
    if (excludeId && slot.id === excludeId) continue
    const slotStart = ms(slot.plannedStart)
    const slotEnd = ms(slot.plannedEnd)
    if (slotStart >= pivotEndMs) {
      updates.set(slot.id, {
        plannedStart: toIso(slotStart + deltaMs),
        plannedEnd: toIso(slotEnd + deltaMs),
      })
    }
  }
  return updates
}

/** Новые интервалы слотов после простоя [downtimeStart, downtimeEnd). */
export function computeSlotsAfterDowntime(
  slots: SlotTimeRange[],
  downtimeStartIso: string,
  downtimeEndIso: string,
): Map<string, SlotTimePatch> {
  const dStart = ms(downtimeStartIso)
  const dEnd = ms(downtimeEndIso)
  if (!Number.isFinite(dStart) || !Number.isFinite(dEnd) || dEnd <= dStart) {
    return new Map()
  }

  const updates = new Map<string, SlotTimePatch>()
  let nextAvailable = dEnd

  for (const slot of sortedShiftable(slots)) {
    const slotStart = ms(slot.plannedStart)
    const slotEnd = ms(slot.plannedEnd)
    const duration = slotEnd - slotStart
    if (duration <= 0) continue
    if (slotEnd <= dStart) continue

    if (slotStart >= dEnd) {
      if (nextAvailable > slotStart) {
        const newStartMs = nextAvailable
        const newEndMs = newStartMs + duration
        updates.set(slot.id, { plannedStart: toIso(newStartMs), plannedEnd: toIso(newEndMs) })
        nextAvailable = newEndMs
      } else {
        nextAvailable = Math.max(nextAvailable, slotEnd)
      }
      continue
    }

    if (slotStart >= dStart) {
      const newStartMs = nextAvailable
      const newEndMs = newStartMs + duration
      updates.set(slot.id, { plannedStart: toIso(newStartMs), plannedEnd: toIso(newEndMs) })
      nextAvailable = newEndMs
      continue
    }

    const overlapStart = Math.max(slotStart, dStart)
    const overlapEnd = Math.min(slotEnd, dEnd)
    const overlapMs = Math.max(0, overlapEnd - overlapStart)
    const remaining = duration - overlapMs
    const newEndMs = dEnd + remaining
    updates.set(slot.id, { plannedStart: slot.plannedStart, plannedEnd: toIso(newEndMs) })
    nextAvailable = Math.max(nextAvailable, newEndMs)
  }

  return updates
}

/** Перенос слота на новое время (тот же станок) + каскад последующих. */
export function computeMoveOnSameMachine(
  slots: SlotTimeRange[],
  movedSlotId: string,
  newStartIso: string,
  newEndIso: string,
): Map<string, SlotTimePatch> {
  const moved = slots.find((s) => s.id === movedSlotId)
  if (!moved) return new Map()

  const oldEnd = ms(moved.plannedEnd)
  const newStart = ms(newStartIso)
  const newEnd = ms(newEndIso)
  if (!Number.isFinite(newStart) || !Number.isFinite(newEnd) || newEnd <= newStart) {
    return new Map()
  }

  const updates = new Map<string, SlotTimePatch>()
  updates.set(movedSlotId, { plannedStart: newStartIso, plannedEnd: newEndIso })

  const deltaEnd = newEnd - oldEnd
  const following = computeShiftFromPivot(slots, oldEnd, deltaEnd, movedSlotId)
  for (const [id, patch] of following) {
    updates.set(id, patch)
  }
  return updates
}

/** Сжать очередь после освобождения интервала [freedStart, freedEnd). */
export function computeCompressQueue(
  slots: SlotTimeRange[],
  freedStartIso: string,
  freedEndIso: string,
  excludeId?: string,
): Map<string, SlotTimePatch> {
  const freedStart = ms(freedStartIso)
  const freedEnd = ms(freedEndIso)
  const freedMs = freedEnd - freedStart
  if (freedMs <= 0) return new Map()
  return computeShiftFromPivot(slots, freedEnd, -freedMs, excludeId)
}

/** Вставка слота на целевом станке с каскадом. */
export function computeInsertOnMachine(
  slots: SlotTimeRange[],
  inserted: { id: string; plannedStart: string; plannedEnd: string },
): Map<string, SlotTimePatch> {
  const insStart = ms(inserted.plannedStart)
  const insEnd = ms(inserted.plannedEnd)
  const duration = insEnd - insStart
  if (duration <= 0) return new Map()

  const updates = new Map<string, SlotTimePatch>()
  updates.set(inserted.id, { plannedStart: inserted.plannedStart, plannedEnd: inserted.plannedEnd })

  let nextAvailable = insEnd
  for (const slot of sortedShiftable(slots)) {
    if (slot.id === inserted.id) continue
    const slotStart = ms(slot.plannedStart)
    const slotEnd = ms(slot.plannedEnd)
    const slotDuration = slotEnd - slotStart
    if (slotDuration <= 0) continue

    if (slotStart >= insStart) {
      if (nextAvailable > slotStart) {
        const newStartMs = nextAvailable
        const newEndMs = newStartMs + slotDuration
        updates.set(slot.id, { plannedStart: toIso(newStartMs), plannedEnd: toIso(newEndMs) })
        nextAvailable = newEndMs
      } else {
        nextAvailable = Math.max(nextAvailable, slotEnd)
      }
    }
  }
  return updates
}

export function computeTransferTimes(
  slot: Pick<SlotTimeRange, 'plannedStart' | 'plannedEnd' | 'status'>,
  plannedQuantity: number,
  targetMachine: Pick<
    MachineRecord,
    'output_quantity' | 'output_period' | 'shift_hours' | 'output_unit'
  >,
  product?: Pick<ProductRecord, 'outputUnit' | 'sheetsPerUnit'> | null,
  newStartIso?: string,
): { plannedStart: string; plannedEnd: string } | null {
  const startIso = newStartIso ?? slot.plannedStart
  const startMs = ms(startIso)
  if (!Number.isFinite(startMs)) return null

  const hours = hoursForProductQuantity(targetMachine, plannedQuantity, product)
  if (hours <= 0) return null

  const endMs = startMs + hours * 3600000
  return { plannedStart: toIso(startMs), plannedEnd: toIso(endMs) }
}

export async function loadMachineSlots(machineId: string): Promise<ScheduleSlotRecord[]> {
  return pb.collection(COL_SCHEDULE_SLOTS).getFullList<ScheduleSlotRecord>({
    filter: `machine="${machineId}" && (status="planned" || status="in_progress")`,
    sort: 'plannedStart',
  })
}

export async function applySlotPatches(
  patches: Map<string, SlotPatch>,
): Promise<number> {
  let count = 0
  for (const [id, patch] of patches) {
    await pb.collection(COL_SCHEDULE_SLOTS).update(id, patch)
    count++
  }
  return count
}

export async function syncRequestLineStatus(lineId: string): Promise<void> {
  if (!lineId) return
  const line = await pb.collection(COL_PRODUCTION_REQUEST_LINES).getOne<ProductionRequestLineRecord>(lineId)
  const slots = await pb.collection(COL_SCHEDULE_SLOTS).getFullList<ScheduleSlotRecord>({
    filter: `requestLine="${lineId}"`,
  })
  const nextStatus = computeLineStatus(line, slots)
  if (nextStatus !== line.status) {
    await pb.collection(COL_PRODUCTION_REQUEST_LINES).update(lineId, { status: nextStatus })
  }

  const requestId = typeof line.request === 'string' ? line.request : line.request
  if (!requestId) return

  const allLines = await pb.collection(COL_PRODUCTION_REQUEST_LINES).getFullList<ProductionRequestLineRecord>({
    filter: `request="${requestId}"`,
  })
  const reqStatus = rollupRequestStatus(allLines)
  const req = await pb.collection(COL_PRODUCTION_REQUESTS).getOne(requestId)
  if (req.status !== reqStatus) {
    await pb.collection(COL_PRODUCTION_REQUESTS).update(requestId, { status: reqStatus })
  }
}

export async function syncRequestLineStatuses(lineIds: Iterable<string>): Promise<void> {
  const seen = new Set<string>()
  for (const id of lineIds) {
    if (!id || seen.has(id)) continue
    seen.add(id)
    await syncRequestLineStatus(id)
  }
}

export type MoveSlotResult = { updated: number; lineIds: string[] }

export async function moveSlotSameMachine(
  slotId: string,
  newStartIso: string,
  newEndIso: string,
): Promise<MoveSlotResult> {
  const slot = await pb.collection(COL_SCHEDULE_SLOTS).getOne<ScheduleSlotRecord>(slotId)
  const machineId = slot.machine
  const slots = await loadMachineSlots(machineId)
  const patches = computeMoveOnSameMachine(slots, slotId, newStartIso, newEndIso)
  const updated = await applySlotPatches(patches)
  const lineId = typeof slot.requestLine === 'string' ? slot.requestLine : ''
  if (lineId) await syncRequestLineStatus(lineId)
  return { updated, lineIds: lineId ? [lineId] : [] }
}

export type TransferSlotOptions = {
  targetMachineId: string
  newStartIso?: string
  compressSourceQueue?: boolean
  product?: Pick<ProductRecord, 'outputUnit' | 'sheetsPerUnit'> | null
  targetMachine: Pick<
    MachineRecord,
    'output_quantity' | 'output_period' | 'shift_hours' | 'output_unit'
  >
  createdBy?: string
}

export async function transferSlotToMachine(
  slotId: string,
  options: TransferSlotOptions,
): Promise<MoveSlotResult> {
  const slot = await pb.collection(COL_SCHEDULE_SLOTS).getOne<ScheduleSlotRecord>(slotId)
  const sourceMachineId = slot.machine
  const qty = Number(slot.plannedQuantity) || 0

  const times = computeTransferTimes(
    slot,
    qty,
    options.targetMachine,
    options.product,
    options.newStartIso,
  )
  if (!times) throw new Error('Не удалось рассчитать время на целевом станке.')

  const lineIds = new Set<string>()
  const lineId = typeof slot.requestLine === 'string' ? slot.requestLine : ''
  if (lineId) lineIds.add(lineId)

  let updated = 0

  if (options.compressSourceQueue && sourceMachineId !== options.targetMachineId) {
    const sourceSlots = await loadMachineSlots(sourceMachineId)
    const compress = computeCompressQueue(
      sourceSlots,
      slot.plannedStart,
      slot.plannedEnd,
      slotId,
    )
    updated += await applySlotPatches(compress)
  }

  const targetSlots = await loadMachineSlots(options.targetMachineId)
  const insertPatches = computeInsertOnMachine(targetSlots, {
    id: slotId,
    plannedStart: times.plannedStart,
    plannedEnd: times.plannedEnd,
  })

  await pb.collection(COL_SCHEDULE_SLOTS).update(slotId, {
    machine: options.targetMachineId,
    plannedStart: times.plannedStart,
    plannedEnd: times.plannedEnd,
  })
  updated += 1

  for (const [id, patch] of insertPatches) {
    if (id === slotId) continue
    await pb.collection(COL_SCHEDULE_SLOTS).update(id, patch)
    updated++
  }

  if (lineId) await syncRequestLineStatus(lineId)
  return { updated, lineIds: [...lineIds] }
}

export async function cancelSlotOperation(
  slotId: string,
  options: { compressQueue?: boolean; createdBy?: string },
): Promise<MoveSlotResult> {
  const slot = await pb.collection(COL_SCHEDULE_SLOTS).getOne<ScheduleSlotRecord>(slotId)
  const prevStatus = slot.status

  await transitionSlotStatus(slotId, 'cancelled', { createdBy: options.createdBy })

  let updated = 1
  const lineId = typeof slot.requestLine === 'string' ? slot.requestLine : ''

  if (options.compressQueue && isShiftableSlot(prevStatus)) {
    const slots = await loadMachineSlots(slot.machine)
    const compress = computeCompressQueue(slots, slot.plannedStart, slot.plannedEnd, slotId)
    updated += await applySlotPatches(compress)
  }

  if (lineId) await syncRequestLineStatus(lineId)
  return { updated, lineIds: lineId ? [lineId] : [] }
}

export async function stopRequestLine(
  lineId: string,
  options: { createdBy?: string },
): Promise<number> {
  const slots = await pb.collection(COL_SCHEDULE_SLOTS).getFullList<ScheduleSlotRecord>({
    filter: `requestLine="${lineId}" && (status="planned" || status="in_progress")`,
  })
  let count = 0
  for (const slot of slots) {
    await cancelSlotOperation(slot.id, { compressQueue: false, createdBy: options.createdBy })
    count++
  }
  await pb.collection(COL_PRODUCTION_REQUEST_LINES).update(lineId, { status: 'cancelled' })
  const line = await pb.collection(COL_PRODUCTION_REQUEST_LINES).getOne(lineId)
  const requestId = typeof line.request === 'string' ? line.request : ''
  if (requestId) {
    const allLines = await pb.collection(COL_PRODUCTION_REQUEST_LINES).getFullList<ProductionRequestLineRecord>({
      filter: `request="${requestId}"`,
    })
    await pb.collection(COL_PRODUCTION_REQUESTS).update(requestId, {
      status: rollupRequestStatus(allLines),
    })
  }
  return count
}

export async function pauseRequestLine(
  lineId: string,
  options: { createdBy?: string; includeInProgress?: boolean },
): Promise<number> {
  let filter = `requestLine="${lineId}" && status="planned"`
  if (options.includeInProgress) {
    filter = `requestLine="${lineId}" && (status="planned" || status="in_progress")`
  }
  const slots = await pb.collection(COL_SCHEDULE_SLOTS).getFullList<ScheduleSlotRecord>({ filter })
  let count = 0
  for (const slot of slots) {
    await cancelSlotOperation(slot.id, { compressQueue: true, createdBy: options.createdBy })
    count++
  }
  await pb.collection(COL_PRODUCTION_REQUEST_LINES).update(lineId, { status: 'pending' })
  return count
}

/** Пересчёт резерва материалов при переносе слота in_progress. */
export async function refreshInProgressReservation(
  slot: ScheduleSlotRecord,
  productId: string,
  createdBy?: string,
): Promise<void> {
  if (slot.status !== 'in_progress') return
  await unreserveMaterialsForSlot(slot.id, createdBy)
  await reserveMaterialsForSlot(slot, productId, createdBy)
}

export async function applyDowntimeToSchedule(
  machineId: string,
  downtimeStartIso: string,
  downtimeEndIso: string,
): Promise<number> {
  if (!machineId) return 0
  const slots = await loadMachineSlots(machineId)
  const patches = computeSlotsAfterDowntime(slots, downtimeStartIso, downtimeEndIso)
  const count = await applySlotPatches(patches)

  const lineIds = new Set<string>()
  for (const slot of slots) {
    const lid = typeof slot.requestLine === 'string' ? slot.requestLine : ''
    if (lid) lineIds.add(lid)
  }
  await syncRequestLineStatuses(lineIds)
  return count
}
